import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { isSameDayLocal } from 'app/events/helpers/is-same-day-local';
import { generateMultipleEvents } from 'app/events/helpers/multiple-events';
import { isoToDisplayDate } from 'app/events/helpers/timezone-conversion';
import { validationToErrorMessages } from 'core/validators';
import {
  Day,
  add,
  format,
  getDate, getDay, getWeekOfMonth,
  isLastDayOfMonth, lastDayOfMonth,
  previousDay, startOfMonth
} from 'date-fns';
import { CalendarEventInput, CreateCalendarEventsGQL } from 'generated/graphql';
import { finalize } from 'rxjs';

const validationMessages = {
  interval: { required: 'Number interval is required' },
  endDate: {
    required: 'End date is required',
    matDatepickerMin: 'End date cannot be before start date.'
  }
};

@Component({
  selector: 'app-multiple-events-component',
  templateUrl: `./multiple-events.component.html`,
  styleUrls: ['./multiple-events.component.scss'],
})
export class MultipleEventsComponent implements OnInit {
  @Input() originalEvent: CalendarEventInput;
  @Output() onBack: EventEmitter<any> = new EventEmitter();
  @Output() onEventCreated: EventEmitter<any> = new EventEmitter();

  daysOfWeek = [
    { label: 'S', value: 0 },
    { label: 'M', value: 1 },
    { label: 'T', value: 2 },
    { label: 'W', value: 3 },
    { label: 'T', value: 4 },
    { label: 'F', value: 5 },
    { label: 'S', value: 6 }
  ];

  originalStartDate = new Date();
  originalEndDate = new Date();
  originalWeekAndDay = '';
  weekNumber = 0;
  originalTime = '';
  originalDayOfWeek: Day;
  hoursSelections: string[] = [];
  errorMessages = [];
  eventCopies = [];
  showPreview = false;
  isLastDayOfMonth = false;
  originalDate = 0;

  multiEventForm: FormGroup = new FormGroup({
    interval: new FormControl(1, [Validators.required]),
    intervalType: new FormControl('week'),
    selectedDays: new FormControl<number[]>([]),
    monthIntervalType: new FormControl('date'),
    endDate: new FormControl<Date>(null, [Validators.required]),
  });

  isSubmitting = false;

  constructor(
    private _createCalendarEventsGQL: CreateCalendarEventsGQL,
  ) { }

  ngOnInit() {
    this.originalStartDate = isoToDisplayDate(this.originalEvent.startDate, this.originalEvent.timeZone);
    this.originalEndDate = this.originalEvent.endDate ? isoToDisplayDate(this.originalEvent.endDate, this.originalEvent.timeZone) : null;
    this.originalDate = getDate(this.originalStartDate);
    this.originalDayOfWeek = getDay(this.originalStartDate) as Day;
    this.isLastDayOfMonth = isLastDayOfMonth(this.originalStartDate);
    // Week number starting at 0
    this.weekNumber = getWeekOfMonth(this.originalStartDate, { weekStartsOn: getDay(startOfMonth(this.originalStartDate)) as Day }) - 1;

    this.updateTimeInput();
    this.updateDaysOfWeekInput();
    this.updateDayOfMonthInput();

    // For testing
    // this.hoursSelections = ['01:00', '23:00'];
    // this.multiEventForm.patchValue({
    //   endDate: add(this.originalStartDate, { weeks: 1 }),
    //   // selectedDays: [0, 1, 2, 3, 4, 5, 6],
    //   // intervalType: 'month',
    //   // monthIntervalType: 'week'
    // });
  }

  updateTimeInput() {
    this.originalTime = format(this.originalStartDate, 'HH:mm');
    this.hoursSelections = [this.originalTime];
  }

  updateDaysOfWeekInput() {
    this.multiEventForm.patchValue({
      selectedDays: [this.originalDayOfWeek]
    });
    this.daysOfWeek = this.daysOfWeek.map(day => ({
      ...day,
      disabled: day.value === this.originalDayOfWeek
    }));
  }

  updateDayOfMonthInput() {
    if (this.originalDate === 31) {
      // No "On day 31" option, select last day of month instead
      this.multiEventForm.patchValue({
        monthIntervalType: 'lastDay'
      });
    }
    // Day of month selection - i.e. "first Tuesday" or "last Friday"
    const dayOfWeek = format(this.originalStartDate, 'EEEE');
    const lastOfMonth = lastDayOfMonth(this.originalStartDate);
    if (isSameDayLocal(this.originalStartDate, lastOfMonth) ||
      getDay(lastOfMonth) !== this.originalDayOfWeek &&
      isSameDayLocal(this.originalStartDate, previousDay(lastOfMonth, this.originalDayOfWeek))) {
      this.originalWeekAndDay = `last ${dayOfWeek}`;
    } else {
      const weekNumbers = ['first', 'second', 'third', 'fourth', 'fifth'];
      this.originalWeekAndDay = `${weekNumbers[this.weekNumber]} ${dayOfWeek}`;
    }
  }

  addStartTime(event: MatChipInputEvent) {
    const value = event.value;
    if (value) {
      this.hoursSelections = [
        ...this.hoursSelections,
        value
      ].sort();

      // Clear the input value & reset focus so it's on hours, not am/pm
      event.chipInput.clear();
      event.chipInput.inputElement.blur();
      event.chipInput.inputElement.focus();
    }
  }

  removeStartTime(timeToRemove) {
    this.hoursSelections = this.hoursSelections.filter(time => time !== timeToRemove);
  }

  preview() {
    if (!this.multiEventForm.valid) {
      this.errorMessages = validationToErrorMessages(this.multiEventForm, validationMessages);
      return;
    }
    this.errorMessages = [];

    // Generate events until max or endDate is reached
    const maxEvents = 100;
    this.eventCopies = generateMultipleEvents(
      this.originalEvent,
      this.hoursSelections,
      this.multiEventForm.value.interval,
      this.multiEventForm.value.intervalType,
      this.multiEventForm.value.selectedDays,
      this.multiEventForm.value.monthIntervalType,
      this.multiEventForm.value.endDate,
      maxEvents);

    if (this.eventCopies.length > maxEvents) {
      this.errorMessages.push(`You can only create ${maxEvents} events at once.`);
      return;
    }

    this.showPreview = true;
  }

  createEvents() {
    this.isSubmitting = true;
    this._createCalendarEventsGQL
      .mutate(
        {
          input: { calendarEvents: this.eventCopies.filter(e => e.selected).map(e => e.eventDetails) }
        },
        {
          refetchQueries: ['SearchCalendarEvents'],
        })
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
        })
      )
      .subscribe(({ data }) => {
        this.onEventCreated.emit(data.createCalendarEvents);
      });
  }
}
